import React, { useState, useEffect, useCallback } from 'react';
import api, { API_BASE } from '../../utils/api';
import { downloadAuthenticatedUrl } from '../../utils/authenticatedBlobDownload';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import DateInput from '../ui/date-input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Download, RefreshCw } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'sent', label: 'Sent' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'read', label: 'Read' },
  { value: 'failed', label: 'Failed' },
  { value: 'pending', label: 'Pending' },
];

function statusBadge(status) {
  const colors = {
    sent: 'bg-blue-100 text-blue-800',
    delivered: 'bg-green-100 text-green-800',
    read: 'bg-emerald-100 text-emerald-800',
    failed: 'bg-red-100 text-red-800',
    pending: 'bg-amber-100 text-amber-800',
    processing: 'bg-gray-100 text-gray-800',
  };
  const cls = colors[status] || 'bg-muted text-muted-foreground';
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{status || '—'}</span>;
}

export default function WhatsappLogsPage() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    status: '',
    student_id: '',
    class_id: '',
  });

  const fetchLogs = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 50, ...filters };
      Object.keys(params).forEach((k) => { if (params[k] === '') delete params[k]; });
      const res = await api.get('/reports/whatsapp-logs', { params });
      setItems(res.data?.data?.items || []);
      setPagination(res.data?.data?.pagination || { page: 1, pages: 1, total: 0 });
    } catch {
      toast({ title: 'Error', description: 'Failed to load WhatsApp logs', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filters, toast]);

  useEffect(() => {
    fetchLogs(1);
  }, [fetchLogs]);

  const handleExport = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    const url = `${API_BASE}/api/reports/whatsapp-logs/xlsx?${params.toString()}`;
    try {
      await downloadAuthenticatedUrl(url, 'whatsapp-logs.xlsx');
    } catch {
      toast({ title: 'Export failed', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">WhatsApp Logs</h1>
          <p className="text-sm text-muted-foreground">Delivery history for queued WhatsApp notifications.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => fetchLogs(pagination.page)}><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
          <Button variant="outline" onClick={handleExport}><Download className="w-4 h-4 mr-2" />Export Excel</Button>
        </div>
      </div>

      <Card className="rounded-2xl">
        <CardContent className="p-4 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div><Label>From</Label><DateInput value={filters.date_from} onChange={(v) => setFilters({ ...filters, date_from: v })} className="mt-1" /></div>
          <div><Label>To</Label><DateInput value={filters.date_to} onChange={(v) => setFilters({ ...filters, date_to: v })} className="mt-1" /></div>
          <div>
            <Label>Status</Label>
            <Select value={filters.status || 'all'} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value || 'all'} value={o.value || 'all'}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Student ID</Label><Input value={filters.student_id} onChange={(e) => setFilters({ ...filters, student_id: e.target.value })} className="mt-1" placeholder="Optional" /></div>
          <div className="flex items-end"><Button onClick={() => fetchLogs(1)} className="w-full">Apply filters</Button></div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="text-left p-3 font-medium">Phone</th>
                  <th className="text-left p-3 font-medium">Template</th>
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="text-left p-3 font-medium">Delivery</th>
                  <th className="text-left p-3 font-medium">Student</th>
                  <th className="text-left p-3 font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No logs found</td></tr>
                ) : items.map((row) => (
                  <tr key={row.id} className="border-b border-border/50 hover:bg-muted/30">
                    <td className="p-3 font-mono text-xs">{row.phone_to}</td>
                    <td className="p-3">{row.template_name || row.template_id}</td>
                    <td className="p-3">{statusBadge(row.status)}</td>
                    <td className="p-3">{statusBadge(row.delivery_status)}</td>
                    <td className="p-3">{row.student_id || '—'}</td>
                    <td className="p-3 text-muted-foreground">{row.created_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {pagination.pages > 1 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" disabled={pagination.page <= 1} onClick={() => fetchLogs(pagination.page - 1)}>Previous</Button>
          <span className="text-sm text-muted-foreground self-center">Page {pagination.page} of {pagination.pages}</span>
          <Button variant="outline" disabled={pagination.page >= pagination.pages} onClick={() => fetchLogs(pagination.page + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
